import { BillingCycle } from '$/GraphQL/schema/schema';
import {
  Plan,
  PriceTierFragment,
  ProductForAdminFragment,
  ProductFragment,
  ProductKind,
  ProductType,
} from '$/GraphQL/schema/types';

import EDate from '$/shared/frontend/utils/EDate';
import { formatCurrency } from '$/utils/formatCurrency';
import { getAddOn } from './AddOns/AddOns';

export class ProductInfo {
  constructor(protected product: ProductFragment | ProductForAdminFragment) {}

  public get kind(): ProductKind {
    return this.product.kind;
  }

  public get type(): ProductType {
    return this.product.type;
  }

  public get name(): string {
    return this.product.name;
  }

  public get quantity(): number {
    return (this.product as ProductForAdminFragment).quantity ?? 0;
  }

  public get nextQuantity(): number {
    return (this.product as ProductForAdminFragment).scheduledQuantity ?? this.quantity;
  }

  public isNew(): boolean {
    return false;
  }

  public get isSubscribed(): boolean {
    return this.product.isSubscribed;
  }

  public get isUnsubscribed(): boolean {
    return this.price === 0;
  }

  public get isTrialAvailable(): boolean {
    return this.product.isTrialAvailable ?? false;
  }

  public get isTrialCancelled(): boolean {
    return this.product.isTrialCancelled ?? false;
  }

  public get hadTrial(): boolean {
    return this.product.hadTrial ?? false;
  }

  public get trialEnd(): EDate | undefined {
    const trialEnd = this.product.trialEnd;
    if (!trialEnd) return undefined;
    const trialEndDate = new EDate(new Date(trialEnd), 'UTC');
    return trialEndDate;
  }

  public get trialDays(): number {
    return this.product.trialDays ?? 0;
  }

  public get isTrialActive(): boolean {
    return Boolean(this.trialEnd && this.trialEnd.getTime() > Date.now());
  }

  public get activeUntil(): EDate | undefined {
    const activeUntil = (this.product as ProductForAdminFragment).activeUntil;
    if (!activeUntil) return undefined;
    const activeUntilDate = new EDate(new Date(activeUntil), 'UTC');
    if (activeUntilDate.getTime() < Date.now()) {
      return undefined;
    }
    return activeUntilDate;
  }

  public get price(): number {
    return (this.product as ProductForAdminFragment).price ?? 0;
  }

  public get priceTiers(): PriceTierFragment[] {
    return this.product.pricePlan.discountedPricePlan ?? this.product.pricePlan.pricePlan ?? [];
  }

  private getPriceTierFromQuantity(quantity: number): PriceTierFragment | undefined {
    return Object.values(this.priceTiers)
      .sort((a, b) => a.from - b.from)
      .reduce((current: PriceTierFragment | undefined, priceTier: PriceTierFragment) => {
        if (quantity >= priceTier.from) {
          return priceTier;
        }
        return current;
      }, undefined);
  }

  public get currentPriceTier(): PriceTierFragment | undefined {
    return this.getPriceTierFromQuantity(this.quantity);
  }

  public get nextPriceTier(): PriceTierFragment | undefined {
    return this.getPriceTierFromQuantity(this.nextQuantity);
  }

  public get priceTier(): PriceTierFragment | undefined {
    return this.currentPriceTier ?? this.priceTiers[0];
  }

  public get prices(): {
    monthly: number;
    annual: number;
    annualByMonth: number;
  } {
    return {
      monthly: this.priceTier?.monthlyPrice ?? 0,
      annual: this.priceTier?.annualPrice ?? 0,
      annualByMonth: this.priceTier?.annualPricePerMonth ?? 0,
    };
  }

  public get nextPrices(): {
    monthly: number;
    annual: number;
    annualByMonth: number;
  } {
    return {
      monthly: this.priceTier?.monthlyPrice ?? 0,
      annual: this.priceTier?.annualPrice ?? 0,
      annualByMonth: this.priceTier?.annualPricePerMonth ?? 0,
    };
  }

  public getPrice(billingCycle: BillingCycle): number {
    return billingCycle === BillingCycle.Month ? this.prices.monthly : this.prices.annual;
  }

  public getFormattedPrice(billingCycle: BillingCycle): string {
    return formatCurrency(this.getPrice(billingCycle));
  }
}

export class WebinarsProductInfo extends ProductInfo {
  public get additional(): number {
    const priceTier = this.priceTier;
    if (priceTier?.tierId !== Plan.Level3) {
      return 0;
    }
    return Math.max(this.quantity - priceTier.to, 0);
  }

  public get nextAdditional(): number {
    const priceTier = this.nextPriceTier;
    if (priceTier?.tierId !== Plan.Level3) {
      return 0;
    }
    return Math.max(this.nextQuantity - priceTier.to, 0);
  }

  public get isUnsubscribed(): boolean {
    return false;
  }

  public get prices(): {
    monthly: number;
    annual: number;
    annualByMonth: number;
  } {
    return {
      monthly:
        (this.priceTier?.monthlyPrice ?? 0) +
        (this.priceTier?.pricePerAdditional ?? 0) * this.additional,
      annual:
        (this.priceTier?.annualPrice ?? 0) +
        (this.priceTier?.annualPricePerAdditional ?? 0) * this.additional,
      annualByMonth:
        (this.priceTier?.annualPricePerMonth ?? 0) +
        (this.priceTier?.annualPricePerAdditionalPerMonth ?? 0) * this.additional,
    };
  }

  public get nextPrices(): {
    monthly: number;
    annual: number;
    annualByMonth: number;
  } {
    return {
      monthly:
        (this.nextPriceTier?.monthlyPrice ?? 0) +
        (this.nextPriceTier?.pricePerAdditional ?? 0) * this.nextAdditional,
      annual:
        (this.nextPriceTier?.annualPrice ?? 0) +
        (this.nextPriceTier?.annualPricePerAdditional ?? 0) * this.nextAdditional,
      annualByMonth:
        (this.nextPriceTier?.annualPricePerMonth ?? 0) +
        (this.nextPriceTier?.annualPricePerAdditionalPerMonth ?? 0) * this.nextAdditional,
    };
  }

  public get registrantLimit(): number {
    return this.currentPriceTier?.registrantLimit ?? 0;
  }
}

export const getProductInfo = (product: ProductFragment | ProductForAdminFragment): ProductInfo => {
  switch (product.kind) {
    case ProductKind.Webinars:
      return new WebinarsProductInfo(product);
    default:
      return getAddOn(product);
  }
};
